
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import tenant from '../../config/tenant';
import { getAuthKeyFromLocalStorage } from './employeeSlice';

function authHeaders(token) {
  return { 'Content-Type': 'application/json', 'auth-key': token };
}

export const fetchPhysicalLocations = createAsyncThunk(
  'physicalLocation/fetchByServiceNo',
  async ({ serviceNo }, { rejectWithValue }) => {
    if (!serviceNo) return rejectWithValue('Service number is required.');
    const token = getAuthKeyFromLocalStorage();
    if (!token) return rejectWithValue('Authentication required. Please login again.');

    try {
      const url = `${tenant.api.baseUrl}/EMP/GetPhyLocByCode?p_sno=${encodeURIComponent(serviceNo)}`;
      const res = await fetch(url, { method: 'GET', headers: authHeaders(token) });

      if (res.status === 404) return { serviceNo, rows: [] }; 

      if (!res.ok) {
        const text = await res.text().catch(() => `HTTP ${res.status}`);
        return rejectWithValue(text || `HTTP error ${res.status}`);
      }

      const data = await res.json();
      if (data && data.StatusCode !== undefined && data.StatusCode !== 200) {
        
        if (/no\s*(data|record)/i.test(data.Message || '')) return { serviceNo, rows: [] };
        return rejectWithValue(data.Message || `Server error: status ${data.StatusCode}`);
      }

      const resultSet = data ? data.ResultSet : null;
      const rows = Array.isArray(resultSet) ? resultSet : (resultSet ? [resultSet] : []);
      return { serviceNo, rows };
    } catch (err) {
      return rejectWithValue(err.message || 'Network error. Check your connection.');
    }
  }
);

export const addPhysicalLocation = createAsyncThunk(
  'physicalLocation/add',
  async ({ serviceNo, divCode, depCode, locCode, startDate, endDate }, { rejectWithValue }) => {
    if (!serviceNo) return rejectWithValue('Service number is required.');
    if (!divCode)   return rejectWithValue('Division is required.');
    if (!depCode)   return rejectWithValue('Department is required.');
    if (!locCode)   return rejectWithValue('Location is required.');
    if (!startDate) return rejectWithValue('Start date is required.');

    const token = getAuthKeyFromLocalStorage();
    if (!token) return rejectWithValue('Authentication required. Please login again.');

    try {
      const res = await fetch(`${tenant.api.baseUrl}/EMP/AddPhyLoc`, {
        method: 'POST',
        headers: authHeaders(token),
        body: JSON.stringify({
          p_sno:    serviceNo,
          p_hpd_div_code:  divCode,
          p_hpd_dep_code:  depCode,
          p_hpd_loc_code:  locCode,
          p_hpd_start_date: startDate,
          p_hpd_end_date:   endDate || '',
        }),
      });

      if (!res.ok) {
        const text = await res.text().catch(() => `HTTP ${res.status}`);
        return rejectWithValue(text || `HTTP error ${res.status}`);
      }

      const data = await res.json().catch(() => ({}));
      if (data && data.StatusCode !== undefined && data.StatusCode !== 200) {
        return rejectWithValue(data.Message || `Server error: status ${data.StatusCode}`);
      }

      return { serviceNo };
    } catch (err) {
      return rejectWithValue(err.message || 'Network error. Check your connection.');
    }
  }
);

const initialState = {
  serviceNo: null,
  rows: [],
  loading: false,
  error: null,
  adding: false,
  addError: null,
  addSuccess: false,
  
  
  
  cache: {},
};

const physicalLocationSlice = createSlice({
  name: 'physicalLocation',
  initialState,
  reducers: {
    clearPhysicalLocationAddState(state) {
      state.adding = false;
      state.addError = null;
      state.addSuccess = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPhysicalLocations.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.serviceNo = action.meta.arg.serviceNo;
      })
      .addCase(fetchPhysicalLocations.fulfilled, (state, action) => {
        const { serviceNo, rows } = action.payload;
        state.loading = false;
        state.serviceNo = serviceNo;
        state.rows = rows;
        state.cache[serviceNo] = rows;
      })
      .addCase(fetchPhysicalLocations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? 'Failed to load physical locations';
        state.rows = [];
      })
      .addCase(addPhysicalLocation.pending, (state) => {
        state.adding = true;
        state.addError = null;
        state.addSuccess = false;
      })
      .addCase(addPhysicalLocation.fulfilled, (state, action) => {
        state.adding = false;
        state.addSuccess = true;
        
        delete state.cache[action.payload.serviceNo];
      })
      .addCase(addPhysicalLocation.rejected, (state, action) => {
        state.adding = false;
        state.addError = action.payload ?? 'Failed to add physical location';
      });
  },
});

export const { clearPhysicalLocationAddState } = physicalLocationSlice.actions;
export default physicalLocationSlice.reducer;
